/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// material
import { Group, CardControl, IncDecField, Control, ListTable, Button } from '@insticator/insticator-ui'; // iui-material
import ConfigController from 'material/expandablesection/controllers/configcontroller/configcontroller';
import ExpandableSection from 'material/expandablesection/expandablesection';
import SectionDescription from 'material/expandablesection/sectiondescription/sectiondescription';
import SectionCard from 'material/expandablesection/sectioncard/sectioncard';
import SectionGroup from 'material/expandablesection/sectiongroup/sectiongroup';
import SectionGroupDescription from 'material/expandablesection/sectiongroupdescription/sectiongroupdescription'
// utils
import { url } from 'utils/urls';
import { updateCommentsTree, updateCommentsTreeData, cancelSectionChanges } from 'state/actions/commentingState/configurationStatus';
// styles
import injectSheet from 'react-jss';
import styles from 'pages/dashboard/commenting/configuration/commentstreesection/commentstreesection_styles';

const CommentsTreeSection = (props) => {

    const limitOptions = [5, 10, 15, 20, 25];

    // redux state
    const updated = useSelector(({ commentingState }) => commentingState.configStatus.commentsTree.updated);
    const commentsTree = useSelector(({ commentingState }) => commentingState.configStatus.commentsTree);
    const savingCommentsTree = useSelector(({ commentingState }) => commentingState.configStatus.savingCommentsTree);
    const treeType = useSelector(({ commentingState }) => commentingState.configStatus.commentsTree.treeType);
    const commentsInitialLoadLimit = useSelector(({ commentingState }) => commentingState.configStatus.commentsTree.commentsInitialLoadLimit);
    const repliesInitialLoadLimit = useSelector(({ commentingState }) => commentingState.configStatus.commentsTree.repliesInitialLoadLimit);
    const commentsLoadMoreLimit = useSelector(({ commentingState }) => commentingState.configStatus.commentsTree.commentsLoadMoreLimit);
    const repliesLoadMoreLimit = useSelector(({ commentingState }) => commentingState.configStatus.commentsTree.repliesLoadMoreLimit);

    const isCustomTreeType = treeType === 'CUSTOM';


    // redux actions
    const dispatch = useDispatch();
    const updateCommentsTreeState = (prop, value) => dispatch(updateCommentsTree(prop, value));
    const restoreStateOnCancel = (section) => dispatch(cancelSectionChanges(section));

    // component state
    const [isOpened, setIsOpened] = useState(false);
    const [commentsInitialLoadLimitCustom, updateCustomCommentsLoadLimit] = useState(isCustomTreeType ? commentsInitialLoadLimit : 5);
    const [repliesInitialLoadLimitCustom, updateCustomRepliesLoadLimit] = useState(isCustomTreeType ? repliesInitialLoadLimit : 1);
    const treeTypeName = `${treeType.charAt(0).toUpperCase()}${treeType.substring(1).toLowerCase()}`.trim();

    // life cycle
    useEffect(() => {
       if (
           (commentsInitialLoadLimitCustom === commentsInitialLoadLimit
           && repliesInitialLoadLimitCustom === repliesInitialLoadLimit)
           || !isCustomTreeType
       ) return;
        updateCommentsTreeState('updated', true)
    },[commentsInitialLoadLimitCustom, repliesInitialLoadLimitCustom]);

    const collapse = () => setIsOpened(false);

    const commentLoad = isCustomTreeType ? commentsInitialLoadLimitCustom : commentsInitialLoadLimit;
    const replyLoad = isCustomTreeType ? repliesInitialLoadLimitCustom : repliesInitialLoadLimit;


    const handleSave = () => {

        const commentsTreeData = {
            ...commentsTree,
            commentsInitialLoadLimit: commentLoad,
            repliesInitialLoadLimit: replyLoad,
        }

        dispatch(updateCommentsTreeData(commentsTree.uuid, commentsTreeData, collapse));

    }

    const handleCancel = () => {
        restoreStateOnCancel('commentsTree');
    }

    const handleSelectTreeType = type => {
        let selectedTreeTypeLimits = {
            commentsInitialLoadLimit: 5,
            repliesInitialLoadLimit: 1
        }

        switch(type) {
            case 'BASIC':
                selectedTreeTypeLimits.commentsInitialLoadLimit = 5;
                selectedTreeTypeLimits.repliesInitialLoadLimit = 1;
                break;
            case 'COMPACT':
                selectedTreeTypeLimits.commentsInitialLoadLimit = 2;
                selectedTreeTypeLimits.repliesInitialLoadLimit = 0;
                break;
            case 'ADVANCED':
                selectedTreeTypeLimits.commentsInitialLoadLimit = 10;
                selectedTreeTypeLimits.repliesInitialLoadLimit = 3;
                break;
            case 'CUSTOM':
                selectedTreeTypeLimits.commentsInitialLoadLimit = commentsInitialLoadLimitCustom;
                selectedTreeTypeLimits.repliesInitialLoadLimit = repliesInitialLoadLimitCustom;
                break;
            default:
                break;
        }

        updateCommentsTreeState('treeType', type);
        updateCommentsTreeState('commentsInitialLoadLimit', selectedTreeTypeLimits.commentsInitialLoadLimit);
        updateCommentsTreeState('repliesInitialLoadLimit', selectedTreeTypeLimits.repliesInitialLoadLimit);
    }

    const controller = (
        <ConfigController
            isOpened={isOpened}
            setIsOpened={setIsOpened}
            handleCancel={handleCancel}
            handleSave={handleSave}
            disableSave={!updated}
            processing={savingCommentsTree}
            title='Comments Tree'
            description={<SectionDescription
                main={treeTypeName}
                additional={`${commentLoad} ${commentLoad > 1 ? 'comments' : 'comment'} / 
                ${replyLoad} ${replyLoad > 1 ? 'replies' : 'reply'}`}
            />}
        />
    );

  return (
    <ExpandableSection
        className={`commentstreesection ${props.classes.commentstreesection}`}
        controller={controller}
        isOpened={isOpened}
        setIsOpened={setIsOpened}
        handleCancel={handleCancel}
        handleSave={handleSave}
        disableSave={!updated}
        processing={savingCommentsTree}
    >
        <div className={'commentstreecontent'}>
            <div className={'commentstreeoptions'}>
                <SectionGroup>
                    <Group>
                        <CardControl
                            title={'Basic'}
                            description={'Perfect for any type of community. Create a balance between showing the most recent comments, and inviting to join a conversation.'}
                            tips={'(entertainment sites, e-commerce sites, etc.)'}
                            image={`${url.image}/illustrations/illustration-basic.svg`}
                            value={'BASIC'}
                            handleSelect={val => handleSelectTreeType(val)}
                            selected={treeType === 'BASIC'}
                            recommended={true}
                        />
                        <CardControl
                            title={'Compact'}
                            description={'Show enough unique opinions to spark a conversation; great for limited real estate.'}
                            tips={'(personal blogs, product sites, etc.)'}
                            image={`${url.image}/illustrations/illustration-compact.svg`}
                            value={'COMPACT'}
                            handleSelect={val => handleSelectTreeType(val)}
                            selected={treeType === 'COMPACT'}
                        />
                        <CardControl
                            title={'Advanced'}
                            description={'Show the most detailed conversation thread, so replies don’t go unnoticed and your readers get the full context of the conversation.'}
                            tips={'(news sites, social sites, educational sites, etc.)'}
                            image={`${url.image}/illustrations/illustration-advanced.svg`}
                            value={'ADVANCED'}
                            handleSelect={val => handleSelectTreeType(val)}
                            selected={treeType === 'ADVANCED'}
                        />
                        { isOpened &&
                            <CardControl
                                title={'Custom'}
                                description={'Define your own presets for how many comments and replies should be visible by default.'}
                                content={
                                    <div
                                        className={'customfields'}
                                        onClick={e => {
                                            e.stopPropagation();
                                            if (treeType !== 'CUSTOM') handleSelectTreeType('CUSTOM')
                                        }}
                                    >
                                        <div className={`customfieldscontent`}>
                                            <label>Visible Comments</label>
                                            <IncDecField
                                                key={treeType}
                                                name={'visiblecomments'}
                                                defaultValue={commentsInitialLoadLimitCustom}
                                                disabled={treeType !== 'CUSTOM'}
                                                minValue={1}
                                                maxValue={25}
                                                handleIncToggle={val => updateCustomCommentsLoadLimit(val)}
                                                handleDecToggle={val => updateCustomCommentsLoadLimit(val)}
                                                ctaPosition={'inline'}
                                            />
                                        </div>
                                        <div className={`${treeType !== 'CUSTOM' ? 'disabled' : ''} customfieldscontent`}>
                                            <label>Visible Replies</label>
                                            <IncDecField
                                                key={treeType}
                                                name={'visiblereplies'}
                                                defaultValue={repliesInitialLoadLimitCustom}
                                                disabled={treeType !== 'CUSTOM'}
                                                minValue={0}
                                                maxValue={25}
                                                handleIncToggle={val => updateCustomRepliesLoadLimit(val)}
                                                handleDecToggle={val => updateCustomRepliesLoadLimit(val)}
                                                ctaPosition={'inline'}
                                            />
                                        </div>
                                    </div>
                                }
                                tips={'(max value for each item is 25)'}
                                image={`${url.image}/illustrations/illustration-custom.svg`}
                                value={'CUSTOM'}
                                handleSelect={val => handleSelectTreeType(val)}
                                selected={isCustomTreeType}
                            />
                        }
                    </Group>
                </SectionGroup>
            </div>

            <div className={'commentstreepreview'}>
                <SectionCard>
                    <div className={'previewcontent'}>
                        <h2>{treeTypeName} tree</h2>
                        <p>Visible by default</p>
                        <ListTable
                            name={'limitcounts'}
                            type={'standard'}
                            variant={'standard'}
                            size={'full'}
                            paginationLocation={'bottom'}
                            data={[
                                {
                                    comments: commentLoad,
                                    replies: replyLoad
                                }]}
                            columnHeads={{
                                comments: <>
                                    <span className={'headicon commentsicon'}>C</span>
                                    <span className={'headtext commentstext'}>Comments</span>
                                </>,
                                replies: <>
                                    <span className={'headicon repliesicon'}>R</span>
                                    <span className={'headtext repliestext'}>Replies</span>
                                </>,
                            }}
                            rowsPerPage={1}
                            fixedColWidth={50}
                            moreDetails={false}
                            loading={false}
                        />
                        <p>Structure</p>
                        <img
                            className={'previewimg'}
                            src={`${url.image}/illustrations/illustration-${isCustomTreeType ? `${treeType.toLowerCase()}- tree` : `${treeType.toLowerCase()}-tree`}.svg`}
                        />
                    </div>
                </SectionCard>
            </div>
        </div>


        <div className={'limitsection'}>
            <SectionCard>
                <div className={'commentslimit'}>
                    <img className={'commentslimiticon'} src={`${url.image}/illustrations/illustration-limit.svg`} />
                    <SectionGroup>
                        <SectionGroupDescription
                            title='Comments Limit'
                            description="Define how many comments should be visible after pressing 'Load more comments’."
                        />
                        <div className={'controlgroup'}>
                            <Group>
                            { limitOptions.map(value =>
                                <Control
                                    text={value.toString()}
                                    value={value}
                                    handleSelect={() =>  updateCommentsTreeState('commentsLoadMoreLimit', parseInt(value))}
                                    variant={'secondary'}
                                    selected={parseInt(commentsLoadMoreLimit) === value}
                                />
                            )}
                            </Group>
                        </div>
                    </SectionGroup>
                </div>

                <div className={'replieslimit'}>
                    <SectionGroup>
                        <SectionGroupDescription
                            title='Replies Limit'
                            description="Define how many replies should be visible after pressing 'Show more replies'."
                        />
                        <div className={'controlgroup'}>
                            <Group>
                            { limitOptions.map(value =>
                                <Control
                                    text={value.toString()}
                                    value={value}
                                    handleSelect={() =>  updateCommentsTreeState('repliesLoadMoreLimit', parseInt(value))}
                                    variant={'secondary'}
                                    selected={parseInt(repliesLoadMoreLimit) === value}
                                />
                            )}
                            </Group>
                        </div>
                    </SectionGroup>
                </div>
            </SectionCard>
        </div>

    </ExpandableSection>
  );
}

export default injectSheet(styles)(CommentsTreeSection)
